import React, { useEffect, useState } from 'react'
import { AiOutlineSearch } from "react-icons/ai"
import { Btn, COLORS, Chip, EmptyState, INPUT_STYLE, fmtStamp, useNarrow } from '../FieldOpsKit'
import { DEMO_USERS } from '../demoData'
import { metaOf, threadOf, unreadFor } from '../store'
import ChatThread from '../field/ChatThread'
import { Avatar } from '../field/fieldUi'

/**
 * Messages (administrators): one conversation per salesperson / driver. Anyone with Field Ops access can answer; the
 * worker sees the reply in the Chat tab of the field app. Conversations can be marked resolved.
 */
const QUICK = [
    { text: 'Got it, thank you.', topic: 'general' }, { text: 'Approved. Go ahead.', topic: 'general' }, { text: 'Not possible today, sorry. I will explain.', topic: 'general' },
    { text: 'Please send a photo of it.', topic: 'general' }, { text: 'I will call you in a few minutes.', topic: 'general' }, { text: 'Skip that store today and continue with the route.', topic: 'schedule' },
]

export default function MessagesTab({ ctx }) {
    const { state, dispatch, me } = ctx
    const field = state.field
    const narrow = useNarrow(900)
    const people = DEMO_USERS.filter((u) => u.kind === 'rep' || u.kind === 'driver')
    const [filter, setFilter] = useState('all')
    const [q, setQ] = useState('')
    const [sel, setSel] = useState(null)

    const rows = people.map((u) => {
        const msgs = threadOf(field, u.id)
        const last = msgs.length ? msgs[msgs.length - 1] : null
        return { u: u, msgs: msgs, last: last, meta: metaOf(field, u.id), unread: unreadFor(field, u.id, 'manager') }
    }).filter((r) => {
        if (q.trim() && (r.u.name + ' ' + (r.last ? r.last.text : '')).toLowerCase().indexOf(q.trim().toLowerCase()) < 0) return false
        if (filter === 'unread') return r.unread > 0
        if (filter === 'open') return !r.meta.resolved && r.msgs.length > 0
        if (filter === 'resolved') return r.meta.resolved
        return true
    }).sort((a, b) => (b.unread > 0) - (a.unread > 0) || (b.last ? b.last.at : 0) - (a.last ? a.last.at : 0))

    const current = sel ? rows.concat(people.filter((u) => !rows.some((r) => r.u.id === u.id)).map((u) => ({ u: u, msgs: threadOf(field, u.id), last: null, meta: metaOf(field, u.id), unread: 0 }))).filter((r) => r.u.id === sel)[0] : null
    const active = current || (!narrow && rows.length ? rows[0] : null)
    const activeId = active ? active.u.id : null
    const activeUnread = active ? active.unread : 0

    useEffect(() => { if (activeId && activeUnread > 0) dispatch({ type: 'CHAT_READ', actorKey: activeId, side: 'manager' }) }, [activeId, activeUnread]) // eslint-disable-line react-hooks/exhaustive-deps

    const totalUnread = rows.reduce((a, r) => a + r.unread, 0)
    const list = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div style={{ position: 'relative' }}><AiOutlineSearch style={{ position: 'absolute', left: 9, top: 10, color: COLORS.muted }} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people or messages" style={{ ...INPUT_STYLE, paddingLeft: 28, width: '100%' }} aria-label="Search messages" /></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['all', 'All'], ['unread', 'Unread' + (totalUnread ? ' (' + totalUnread + ')' : '')], ['open', 'Open'], ['resolved', 'Resolved']].map((f) => <button key={f[0]} type="button" onClick={() => setFilter(f[0])} style={{ border: '1px solid ' + (filter === f[0] ? COLORS.navy : COLORS.line), background: filter === f[0] ? COLORS.navy : '#FFFFFF', color: filter === f[0] ? '#FFFFFF' : COLORS.navy, borderRadius: 999, padding: '0.25rem 0.8rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>{f[1]}</button>)}
            </div>
            {rows.length === 0 ? <EmptyState title="No conversations" text="Change the filter to see more." /> : rows.map((r) => (
                <div key={r.u.id} onClick={() => setSel(r.u.id)} role="button" tabIndex={0} style={{ display: 'flex', gap: 10, alignItems: 'center', background: activeId === r.u.id ? '#EFF6FF' : '#FFFFFF', border: '1px solid ' + (activeId === r.u.id ? '#BFDBFE' : COLORS.line), borderRadius: 12, padding: 10, cursor: 'pointer' }}>
                    <Avatar name={r.u.name} tone={r.u.kind === 'rep' ? 'blue' : 'green'} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}><strong style={{ fontSize: '0.9rem' }}>{r.u.name}</strong><span style={{ fontSize: '0.7rem', color: COLORS.slate }}>{r.last ? fmtStamp(r.last.at) : ''}</span></div>
                        <div style={{ fontSize: '0.78rem', color: COLORS.slate, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.last ? (r.last.from === 'manager' ? 'You: ' : '') + r.last.text : 'No messages yet'}</div>
                    </div>
                    {r.unread > 0 ? <span style={{ background: COLORS.red, color: '#FFFFFF', borderRadius: 999, fontSize: '0.7rem', fontWeight: 800, padding: '1px 7px' }}>{r.unread}</span> : (r.meta.resolved ? <Chip tone="green">Resolved</Chip> : null)}
                </div>
            ))}
        </div>
    )

    const thread = active ? (
        <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                {narrow ? <Btn kind="secondary" small onClick={() => setSel(null)}>‹ Back</Btn> : null}
                <Avatar name={active.u.name} tone={active.u.kind === 'rep' ? 'blue' : 'green'} />
                <div><strong>{active.u.name}</strong><div style={{ fontSize: '0.75rem', color: COLORS.slate }}>{active.u.kind === 'rep' ? 'Salesperson' : 'Driver'}{active.meta.resolved ? ' · resolved' : ''}</div></div>
                <div style={{ marginLeft: 'auto' }}><Btn kind="secondary" small onClick={() => dispatch({ type: 'CHAT_RESOLVE', actorKey: active.u.id, resolved: !active.meta.resolved })}>{active.meta.resolved ? 'Reopen' : 'Mark resolved'}</Btn></div>
            </div>
            <div style={{ height: narrow ? '70vh' : 'calc(100vh - 360px)', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
            <ChatThread messages={active.msgs} side="manager" meta={active.meta} quick={QUICK} placeholder={'Reply to ' + active.u.name}
                onSend={(m) => dispatch({ type: 'CHAT_SEND', actorKey: active.u.id, from: 'manager', by: me.name, text: m.text, topic: m.topic, ref: m.ref, photo: m.photo })} />
            </div>
        </div>
    ) : <EmptyState title="Select a conversation" />

    return (
        <div className="fo-adminchat" style={narrow ? null : { display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
            {narrow ? (sel ? thread : list) : <React.Fragment>{list}{thread}</React.Fragment>}
        </div>
    )
}
